import { Intent } from "@blueprintjs/core";
import { Button } from "components/buttons";
import { Filter, FilterButton } from "components/filtering";
import { ArrayToEllipsesString } from "components/formatting";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { NotificationToaster, AlertConfirm } from "components/notifications";
import { ActiveStatus } from "components/status";
import { Tooltip } from "components/tooltip";
import { useDebounce } from "hooks/useDebounce";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { EmployeeService, EmployeeReviewTypesService } from "services";
import { SetFiltering } from "state/actions/Actions";

const listingRoute = "/employee-reviews/review-types";

// lck.Review_Frequency_Unit is static reference data (ids fixed by the release seed); the search
// response returns only the id, so the listing composes the label client-side.
const frequencyUnitNames = { 1: "Days", 2: "Weeks", 3: "Months", 4: "Years" };

const emptyOptions = { employeeTypes: [], employeeCategories: [], employeeStatuses: [] };

// The Status filter maps to the Status parameter on POST /EmployeeReviewTypes/Search.
// Single-select: no selection means all statuses.
const pageFilters = [
    {
        name: "Status",
        displayName: "Status",
        singleSelect: true,
        items: [
            { id: "ENABLED", name: "Enabled" },
            { id: "DISABLED", name: "Disabled" }
        ]
    }
];

const tableHeaders = ["Review Type", "Schedule", "Applies To", "Status", ""];
const sortableHeaders = [
    { headerName: "Review Type", sortName: "name" },
    { headerName: "Status", sortName: "enabled" }
];

function resolveNames(ids, options) {
    if (ids == null || options == null) {
        return [];
    }
    return ids
        .map((id) => options.find((o) => o.id === id))
        .filter((o) => o != null)
        .map((o) => o.name);
}

function scheduleSummary(schedule) {
    if (schedule == null || !schedule.active) {
        return "Not scheduled";
    }
    const unit = frequencyUnitNames[schedule.frequencyUnitId];
    if (schedule.frequency != null && unit != null) {
        return `Every ${schedule.frequency} ${unit.toLowerCase()}`;
    }
    return "Scheduled";
}

export function ReviewTypesListing() {
    const defaultRecordSize = 25;
    const debounceTimeout = 750;
    const filterName = "employeeReviewTypes";

    const dispatch = useDispatch();

    const [initalSetupComplete, setInitalSetupComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [employeeOptions, setEmployeeOptions] = useState(emptyOptions);

    const [sortedDir, setSortedDir] = useState("A");
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);

    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultRecordSize);
    const [tableRows, setTableRows] = useState([]);

    const [appliedFilters, setAppliedFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [deleting, setDeleting] = useState(false);
    const [selectedReviewTypeId, setSelectedReviewTypeId] = useState(0);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentAppliedFilters = useRef();

    const selectedFilters = useSelector((state) => state.filters[filterName]);

    const noDataMessage = "You have no review types, please click 'New Review Type' to get started.";

    if (!selectedFilters) {
        dispatch(SetFiltering(filterName, []));
    }

    useEffect(() => {
        EmployeeService.getEmployeeOptions()
            .then((o) => {
                setEmployeeOptions(o ?? emptyOptions);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to load employee options. Applies-to detail may be incomplete.");
            })
            .finally(() => {
                setInitalSetupComplete(true);
            });
    }, []);

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => !prevState);
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
        setPageNumber(1);
    }

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onTableSort(header, direction) {
        setSortedDir(direction);
        setSortedBy(header);
    }

    function promptDelete(id) {
        setSelectedReviewTypeId(id);
        setShowDeleteConfirm(true);
    }

    function onDeleteCancel() {
        setSelectedReviewTypeId(0);
        setShowDeleteConfirm(false);
    }

    function onDeleteConfirm() {
        setDeleting(true);

        EmployeeReviewTypesService.deleteReviewType(selectedReviewTypeId)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Review type has been successfully deleted.");
                setSelectedReviewTypeId(0);
                setShowDeleteConfirm(false);
                fetchTableData(true);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Failed to delete review type. Please try again.");
            })
            .finally(() => {
                setDeleting(false);
            });
    }

    function buildRows(items) {
        return items.map((item) => {
            const appliesTo = [
                ...resolveNames(item.employeeTypes, employeeOptions.employeeTypes),
                ...resolveNames(item.employeeCategories, employeeOptions.employeeCategories),
                ...resolveNames(item.employeeStatuses, employeeOptions.employeeStatuses)
            ];

            const hasCompletedReviews = item.completedReviewCount > 0;

            const deleteButton = (
                <Button
                    key={`delete-review-type-${item.reviewTypeId}`}
                    id={`delete-review-type-${item.reviewTypeId}`}
                    icon="trash"
                    text="Delete"
                    iconOnly
                    minimal
                    large={false}
                    disabled={hasCompletedReviews}
                    onClick={() => promptDelete(item.reviewTypeId)}
                />
            );

            return [
                <Link key={`link-review-type-${item.reviewTypeId}`} id={`edit-review-type-${item.reviewTypeId}`} to={`${listingRoute}/${item.reviewTypeId}`}>{item.name}</Link>,
                scheduleSummary(item.schedule),
                appliesTo.length > 0
                    ? <ArrayToEllipsesString key={`applies-to-${item.reviewTypeId}`}>{appliesTo}</ArrayToEllipsesString>
                    : "All employees",
                <ActiveStatus key={`status-${item.reviewTypeId}`} isActive={item.enabled} activeText="Enabled" inactiveText="Disabled" />,
                <div key={`actions-${item.reviewTypeId}`} className="button-row-small">
                    {hasCompletedReviews
                        // A native disabled button suppresses hover events, so wrap it in a span
                        // to give the tooltip a hoverable target (Blueprint's documented approach).
                        ? <Tooltip content="This review type has completed reviews. Disable it instead of deleting."><span>{deleteButton}</span></Tooltip>
                        : deleteButton}
                </div>
            ];
        });
    }

    function fetchTableData(forceRefresh) {
        if (initalSetupComplete) {
            if (
                currentTableSortDir.current === sortedDir &&
                currentPageNumber.current === pageNumber &&
                currentPageSize.current === pageSize &&
                currentTableSortBy.current === sortedBy &&
                currentAppliedFilters.current === appliedFilters &&
                currentSearchTerm.current === debouncedSearchTerm &&
                !forceRefresh
            ) {
                // If none of the values have changed, then 2 have been updated at once, so prevent the listing from performing another call.
                return;
            }

            currentTableSortBy.current = sortedBy;
            currentPageNumber.current = pageNumber;
            currentPageSize.current = pageSize;
            currentTableSortDir.current = sortedDir;
            currentAppliedFilters.current = appliedFilters;
            currentSearchTerm.current = debouncedSearchTerm;

            setIsLoading(true);

            const filters = (appliedFilters ?? []).map((f) => ({ key: f.name, value: f.value }));

            EmployeeReviewTypesService.searchReviewTypes(pageSize, pageNumber, sortedBy.sortName, sortedDir, debouncedSearchTerm, filters)
                .then((r) => {
                    setTableRows(r?.data != null ? buildRows(r.data) : []);
                    setTotalRecords(r?.totalCount ?? 0);
                    setIsLoading(false);
                })
                .catch(() => {
                    NotificationToaster.show(Intent.DANGER, "Failed to get review type listing. Please refresh the page.");
                    setIsLoading(false);
                });
        }
    }

    useEffect(() => {
        fetchTableData(false);
    }, [
        pageNumber,
        pageSize,
        sortedBy,
        sortedDir,
        debouncedSearchTerm,
        initalSetupComplete,
        appliedFilters
    ]);

    return (
        <>
            <div className="filter-controls spacer-bottom">
                <div className="inline-items">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                placeholder="Search review types"
                                onChange={onSearchChange}
                                value={searchTerm}
                                large
                                disabled={isLoading}
                                icon="search"
                                id="review-type-search-field"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                    </div>
                </div>
                <Filter
                    filterName={filterName}
                    filters={pageFilters}
                    visible={showFilters}
                    numOfColumns={3}
                    onUpdate={onFilterChange}
                    onClickOutside={onToggleFilter}
                />
            </div>

            <ListingTable
                id="review-types-listing"
                pageSize={pageSize}
                headers={tableHeaders}
                data={tableRows}
                loadingData={isLoading}
                onPagingChange={onPagingChange}
                totalRecordCount={totalRecords}
                noDataMessage={noDataMessage}
                sortable
                sortedBy={sortedBy}
                sortedDir={sortedDir}
                sortableHeaders={sortableHeaders}
                onSort={onTableSort}
            />

            <AlertConfirm
                title="Please confirm"
                isOpen={showDeleteConfirm}
                onConfirm={onDeleteConfirm}
                onCancel={onDeleteCancel}
                confirmButtonText="Delete"
                saving={deleting}
            >
                <p>Are you sure you want to delete this review type?</p>
            </AlertConfirm>
        </>
    );
}
